import { memo, useEffect, useMemo } from 'react';

import { usePaginationMessage } from '~/hooks/usePagination';
import { PageButtons } from '~/modules/pagination/PageButtons';

export interface PaginationProps {
  page: number;
  perPage: number;
  setPageNumber: (page: number) => void;
  total: number;
  entity?: string;
  showAsLink?: boolean;
}

/**
 * This component is wrapped in React.memo() to prevent unnecessary re-renders with
 * the ScrollCollapse logic. See https://github.com/wmgtech/wmg2-one-app/pull/1736
 */
export const Pagination: React.FC<PaginationProps> = memo(
  ({ page, total, entity, perPage, setPageNumber, showAsLink }) => {
    const totalPages = useMemo(
      () => Math.ceil(total / perPage),
      [total, perPage],
    );

    const message = usePaginationMessage(page, perPage, total, entity);

    useEffect(() => {
      if (totalPages > 0 && page > totalPages) {
        setPageNumber(1);
      }
    }, [page, totalPages, setPageNumber]);

    if (total === 0) {
      return null;
    }

    const getPageLink = (pageNumber: number) => {
      const url = new URL(window.location.href);

      url.searchParams.set('page', pageNumber.toString());

      return url.toString();
    };

    return (
      <div className="ml-auto">
        <div className="inline-flex items-center">
          <span className="text-nowrap pr-sm text-body-sm font-semibold text-subtle">
            {message}
          </span>
          <PageButtons
            onNext={() => setPageNumber(page + 1)}
            onPrevious={() => setPageNumber(page - 1)}
            currentPage={page}
            totalPages={totalPages}
            nextLink={
              showAsLink && page < totalPages
                ? getPageLink(page + 1)
                : undefined
            }
            previousLink={
              showAsLink && page > 1 ? getPageLink(page - 1) : undefined
            }
          />
        </div>
      </div>
    );
  },
);

Pagination.displayName = 'Pagination';
